import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';
import { Link } from 'react-router-dom';

const FaqPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://incendios.justclean.es';

  const faqs = [
    {
      question: '¿Mi seguro cubre la limpieza post incendio?',
      answer: 'En la mayoría de los casos, sí. Las pólizas de hogar suelen incluir cobertura para daños por incendio, que abarca tanto reparaciones estructurales como limpieza de hollín y humo. Sin embargo, cada póliza es diferente. Te recomendamos contactar inmediatamente con tu aseguradora tras el incendio, solicitar la visita del perito y preguntarle específicamente qué trabajos están cubiertos. Nosotros te ayudamos con la documentación necesaria (fotografías, informes, presupuestos) para facilitar la reclamación.',
    },
    {
      question: '¿Cuánto cuesta la limpieza post incendio en Barcelona?',
      answer: 'El coste depende de múltiples factores: metros cuadrados afectados, gravedad del incendio, número de habitaciones, altura del techo, tipo de materiales (pintura, madera, azulejo), volumen de enseres a retirar, accesibilidad (ascensor, escaleras), y si hay tratamientos especiales necesarios (desodorización intensiva, limpieza de conductos). Como referencia orientativa, un piso de 90 m² con afectación moderada puede costar entre 2.000-4.000€, pero cada caso es único. Ofrecemos presupuestos personalizados sin compromiso tras la evaluación inicial.',
    },
    {
      question: '¿Cuánto tiempo tarda la limpieza completa?',
      answer: 'Depende del alcance de los daños. Para un incendio localizado en una cocina (15-20 m²), podemos completar la limpieza en 1-2 días. Para un piso completo de 90 m² con afectación moderada, el proceso suele llevar 3-4 días laborables. En casos de incendios graves con afectación estructural extensa, puede requerir 1-2 semanas. Estos tiempos incluyen limpieza de hollín, desodorización, saneamiento y preparación para pintura. Durante la evaluación inicial te daremos un plazo más preciso según tu caso específico.',
    },
    {
      question: '¿Cómo se elimina el olor a humo de forma permanente?',
      answer: 'El olor a humo es causado por partículas microscópicas que penetran en materiales porosos (paredes, techos, textiles, muebles). Para eliminarlo permanentemente, primero eliminamos físicamente el hollín con aspiración industrial y lavado técnico. Luego aplicamos tratamientos de desodorización profesional: generadores de ozono (que oxidan las moléculas de olor), nebulizadores con productos enzimáticos, y selladores específicos en superficies porosas. En casos graves, puede ser necesario retirar materiales muy impregnados (cortinas, alfombras) y limpiar conductos de ventilación. No usamos ambientadores que solo enmascaran el olor temporalmente.',
    },
    {
      question: '¿Qué se puede salvar y qué hay que tirar después de un incendio?',
      answer: 'Depende del grado de exposición al fuego, hollín y humo. Generalmente se pueden recuperar: muebles de madera maciza (con limpieza profunda), electrodomésticos no expuestos directamente al fuego, objetos de metal, cerámica y vidrio, documentos y fotografías (con técnicas especializadas). Suelen ser irrecuperables: textiles muy impregnados de humo, colchones, almohadas, alimentos, productos de higiene personal, muebles de aglomerado quemados, y cualquier objeto con daño estructural. Durante la evaluación inicial te asesoramos sobre cada elemento específico y coordinamos con el perito para documentar los bienes irrecuperables.',
    },
    {
      question: '¿Se pueden limpiar las paredes y techos o hay que repintar siempre?',
      answer: 'Sí, se pueden limpiar, y de hecho es imprescindible hacerlo antes de repintar. El hollín es ácido y si pintas directamente sobre él, las manchas reaparecerán (efecto "bleeding"). Nuestro proceso incluye: aspiración de partículas, lavado técnico con productos específicos según el tipo de superficie, y aplicación de selladores anti-manchas si es necesario. Una vez limpio y sellado, el pintor puede trabajar sin problemas. En casos de afectación muy grave donde la pintura se ha quemado o desprendido, será necesario reparar el yeso antes de pintar, pero la limpieza previa sigue siendo esencial.',
    },
    {
      question: '¿Qué pasa con la ropa y los textiles afectados por el humo?',
      answer: 'Los textiles requieren tratamiento especializado. La ropa, cortinas, sábanas y toallas con olor a humo no deben lavarse en lavadora doméstica, ya que puede fijar el olor permanentemente. Recomendamos lavanderías especializadas en restauración post siniestro que utilizan productos y técnicas específicas. En casos de impregnación severa, algunos textiles pueden ser irrecuperables. Alfombras y tapicerías de muebles requieren limpieza profesional in situ o en taller especializado. Te asesoramos sobre qué textiles pueden salvarse y coordinamos soluciones con proveedores especializados si lo necesitas.',
    },
    {
      question: '¿Incluye la retirada de escombros y enseres dañados?',
      answer: 'Sí, ofrecemos este servicio bajo petición. Incluye la clasificación de enseres recuperables vs. irrecuperables, desescombro, embalaje y retirada de materiales dañados, y gestión de residuos según normativa. Este servicio se coordina siempre con el perito de la aseguradora para documentar correctamente los bienes que se retiran, ya que es información necesaria para la reclamación. El coste de este servicio se presupuesta por separado según el volumen de residuos y la complejidad del acceso (ascensor, escaleras, distancia al contenedor).',
    },
    {
      question: '¿Se puede vivir en la vivienda durante los trabajos de limpieza?',
      answer: 'Depende de la extensión de los daños. Si el incendio fue localizado (por ejemplo, solo la cocina) y el resto de la vivienda está habitable, es posible permanecer en otras habitaciones mientras trabajamos, aunque habrá molestias (ruido, olores de productos de limpieza, movimiento de personal). Si el incendio afectó a toda la vivienda, recomendamos buscar alojamiento temporal durante los 3-5 días que dura la intervención intensiva, por seguridad, comodidad y para permitirnos trabajar con mayor eficiencia. Muchas pólizas de seguro incluyen cobertura de alojamiento temporal en estos casos.',
    },
    {
      question: '¿Necesito permisos de la comunidad de vecinos para la limpieza?',
      answer: 'Para la limpieza interior de tu vivienda, no necesitas permiso de la comunidad. Sin embargo, es muy recomendable informar al administrador de fincas y a los vecinos, especialmente si vamos a utilizar zonas comunes (escalera, ascensor, portal) para el transporte de materiales o residuos. Nosotros protegemos siempre las zonas comunes con plásticos y lonas para evitar ensuciar, y coordinamos horarios para minimizar molestias. Si el incendio afectó a elementos comunes (fachada, estructura), entonces sí puede ser necesaria autorización de la comunidad para ciertas reparaciones, pero eso ya entra en fase de obra, no de limpieza.',
    },
    {
      question: '¿Qué documentación necesito enviar para el presupuesto?',
      answer: 'Para elaborar un presupuesto preciso, nos ayuda mucho recibir: fotografías o vídeo de todas las zonas afectadas (paredes, techos, suelos, muebles), indicación de los metros cuadrados aproximados de la vivienda, descripción breve de cómo ocurrió el incendio y qué zonas están más afectadas, información sobre si hay ascensor o es necesario subir escaleras, y si ya tienes perito asignado por la aseguradora. Si no tienes toda esta información, no te preocupes: con una llamada inicial podemos coordinar una visita de evaluación y elaborar el presupuesto tras la inspección in situ.',
    },
    {
      question: '¿Cuándo es seguro volver a entrar en la vivienda después del incendio?',
      answer: 'Solo cuando los bomberos y las autoridades competentes hayan declarado el espacio seguro estructuralmente y sin riesgo de reignición. Incluso entonces, el ambiente puede ser peligroso por inhalación de partículas de hollín y gases residuales. Recomendamos ventilar bien (si es seguro abrir ventanas), no tocar superficies con las manos desnudas, y limitar el tiempo de permanencia hasta que se realice la limpieza profesional. Después de nuestra intervención completa (limpieza, desodorización, saneamiento), el espacio es seguro para habitar, aunque aún falten las reparaciones de pintura y obra.',
    },
  ];

  const filteredFaqs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${siteUrl}/faq#faqpage`,
    url: `${siteUrl}/faq`,
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="Preguntas Frecuentes Limpieza Post Incendio | JUSTCLEAN"
        description="Resuelve tus dudas sobre limpieza post incendio: seguros, costes, tiempos, olores, qué se puede salvar. FAQ completo sobre limpieza tras incendio en Barcelona."
        keywords="preguntas limpieza post incendio, FAQ incendio, coste limpieza hollín, seguro cubre limpieza, cuánto tarda limpieza incendio"
        canonical="/faq"
        structuredData={structuredData}
      />

      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Preguntas frecuentes sobre limpieza post incendio
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              Resolvemos las dudas más comunes sobre limpieza de hollín, seguros, costes, tiempos y recuperación tras un incendio en Barcelona.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar pregunta..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-6 py-4 pr-12 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff0000] focus:border-transparent"
                />
                <i className="ri-search-line absolute right-4 top-1/2 transform -translate-y-1/2 text-xl text-gray-400"></i>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-search-line text-5xl text-gray-300 mb-4"></i>
              <p className="text-lg text-gray-600">
                No se encontraron preguntas que coincidan con tu búsqueda.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredFaqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="bg-gray-50 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
                  >
                    <h3 className="text-base font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    <i
                      className={`text-2xl text-[#ff0000] transition-transform flex-shrink-0 ${
                        openIndex === index ? 'ri-subtract-line' : 'ri-add-line'
                      }`}
                    ></i>
                  </button>
                  <AnimatePresence>
                    {openIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-5">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            ¿Tienes más preguntas?
          </h2>
          <p className="text-base text-gray-600 mb-8">
            Si no encontraste la respuesta que buscabas, no dudes en contactarnos directamente. Estaremos encantados de ayudarte.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+34602365996"
              className="px-8 py-4 bg-[#ff0000] text-white text-base font-semibold rounded-lg hover:bg-[#cc0000] transition-all whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Llamar Ahora
            </a>
            <a
              href="https://wa.me/34602365996"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 bg-[#25D366] text-white text-base font-semibold rounded-lg hover:bg-[#1da851] transition-all whitespace-nowrap"
            >
              <i className="ri-whatsapp-line mr-2"></i>
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Quick Tips */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Consejos rápidos post incendio
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Conoce más sobre <Link to="/empresa" className="text-[#ff0000] hover:underline font-semibold">nuestro equipo profesional</Link> y los <Link to="/servicios" className="text-[#ff0000] hover:underline font-semibold">servicios especializados de limpieza</Link> que ofrecemos.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: 'ri-phone-line',
                title: 'Contacta con tu seguro inmediatamente',
                description: 'Avisa a tu aseguradora en las primeras horas y solicita la visita del perito.',
              },
              {
                icon: 'ri-camera-line',
                title: 'Documenta todo con fotos',
                description: 'Fotografía todos los daños antes de tocar o mover nada.',
              },
              {
                icon: 'ri-hand-sanitizer-line',
                title: 'No limpies con productos domésticos',
                description: 'Puedes empeorar las manchas o fijar el hollín en las superficies.',
              },
              {
                icon: 'ri-time-line',
                title: 'Actúa rápido',
                description: 'Cuanto antes se inicie la limpieza profesional, menores serán los daños permanentes.',
              },
              {
                icon: 'ri-air-conditioner-line',
                title: 'No enciendas climatización',
                description: 'Puede dispersar partículas de hollín por todo el sistema de ventilación.',
              },
              {
                icon: 'ri-team-line',
                title: 'Contrata profesionales',
                description: 'La limpieza post incendio requiere técnicas y productos especializados.',
              },
            ].map((tip, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-50 p-6 rounded-lg"
              >
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${tip.icon} text-2xl text-[#ff0000]`}></i>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">
                  {tip.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {tip.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default FaqPage;
